import React from 'react';
import classNames from 'classnames';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { GatesNowDataProvider, useGatesNow } from '../GatesNowDataContext';
import { GateNowMap } from '../pages/GateNowMap';
import { ConcessionsGateNow } from '../pages/ConcessionsGateNow';
import { displayApiCall } from './displayApi';
import { isValidDisplayToken } from './displayConfig';
import { useDisplayHeartbeat } from './useDisplayHeartbeat';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import '../fonts.css';
import '../App.scss';

const TABS = [
	{ label: 'Map', icon: 'pi pi-fw pi-map', suffix: 'map' },
	{ label: 'Table', icon: 'pi pi-fw pi-table', suffix: 'table' },
];

const displayMessageStyle = {
	wrap: { textAlign: 'center', padding: '40px' },
	title: { fontSize: '20px', fontWeight: 'bold', color: '#333', marginTop: '100px' },
	body: { fontSize: '16px', color: '#555', maxWidth: '28rem', margin: '12px auto 0' },
};

const apiStatus = (err) => err?.response?.status ?? err?.status ?? null;

/** Inside GatesNowDataProvider: swap Map/Table for a clear block page on API deny/fail. */
const DisplayMain = ({ isTable }) => {
	const { loading, error } = useGatesNow();
	const status = apiStatus(error);

	if (!loading && status === 403) {
		return (
			<div className="layout-main" style={displayMessageStyle.wrap}>
				<h1 style={displayMessageStyle.title}>Forbidden</h1>
				<p style={displayMessageStyle.body}>
					This display is only available on the approved airport network.
				</p>
			</div>
		);
	}

	if (!loading && error) {
		return (
			<div className="layout-main" style={displayMessageStyle.wrap}>
				<h1 style={displayMessageStyle.title}>Unable to load</h1>
				<p style={displayMessageStyle.body}>
					GatesNow data could not be loaded. Check the network connection and try again.
				</p>
			</div>
		);
	}

	return (
		<div className={classNames('layout-main', isTable ? 'is-table' : 'is-map')}>
			{/* Relative to AppWrapper /display/:token/* */}
			<Routes>
				<Route index element={<Navigate to="map" replace />} />
				<Route path="map" element={<GateNowMap />} />
				<Route path="table" element={<ConcessionsGateNow />} />
				<Route path="*" element={<Navigate to="map" replace />} />
			</Routes>
		</div>
	);
};

const DisplayShell = ({ token }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const base = `/display/${token}`;
	const isTable = /\/table\/?$/.test(location.pathname);
	const activeIndex = isTable ? 1 : 0;

	useDisplayHeartbeat(true);

	const selectTab = (suffix) => {
		const path = `${base}/${suffix}`;
		if (location.pathname !== path) {
			navigate(path);
		}
	};

	return (
		<div
			className={classNames(
				'layout-wrapper',
				'menu-layout-static',
				'layout-menu-static-inactive',
				'has-topbar-tabs',
				'display-mode',
			)}
		>
			<div className="topbar clearfix">
				<div className="topbar-left">
					<button type="button" className="p-link" onClick={() => selectTab('map')}>
						<img
							alt="Logo"
							src="/assets/layout/images/logo.png"
							className="topbar-logo"
						/>
						<div>GatesNow</div>
					</button>
				</div>
				<nav className="gatesnow-tabs gatesnow-tabs--topbar" aria-label="Gates Now views">
					{TABS.map((tab, index) => (
						<button
							key={tab.suffix}
							type="button"
							className={classNames('gatesnow-tab', {
								'is-active': activeIndex === index,
							})}
							aria-current={activeIndex === index ? 'page' : undefined}
							onClick={() => selectTab(tab.suffix)}
						>
							<i className={tab.icon} aria-hidden />
							<span>{tab.label}</span>
						</button>
					))}
				</nav>
				<div className="topbar-right acaa" />
			</div>

			<div className="menu menu--mobile-tabs">
				<div className="col">
					<nav className="gatesnow-tabs" aria-label="Gates Now views">
						{TABS.map((tab, index) => (
							<button
								key={tab.suffix}
								type="button"
								className={classNames('gatesnow-tab', {
									'is-active': activeIndex === index,
								})}
								aria-current={activeIndex === index ? 'page' : undefined}
								onClick={() => selectTab(tab.suffix)}
							>
								<i className={tab.icon} aria-hidden />
								<span>{tab.label}</span>
							</button>
						))}
					</nav>
				</div>
			</div>

			<UserContext.Provider value={{ userInfo: null, apiCall: displayApiCall }}>
				<GatesNowDataProvider>
					<DisplayMain isTable={isTable} />
				</GatesNowDataProvider>
			</UserContext.Provider>
		</div>
	);
};

const DisplayDenied = () => (
	<div className="layout-main" style={displayMessageStyle.wrap}>
		<h1 style={displayMessageStyle.title}>Not found</h1>
		<p style={displayMessageStyle.body}>This display link is invalid or incomplete.</p>
	</div>
);

/** Capability-URL shell: /display/:token[/map|/table] */
export const DisplayApp = () => {
	const { token } = useParams();
	if (!isValidDisplayToken(token)) {
		return <DisplayDenied />;
	}
	return <DisplayShell token={token} />;
};
